import { MAX_PERSONEN, zaehlung } from "../shared/personen.ts";
import type { BildAusListe } from "./lib/api.ts";
import { mehrfachbilder } from "./lib/zaehlpruefung.ts";
import { personenText } from "./Personenzahl.tsx";

/**
 * Der Schluss-Check vor der Vorschau (Annas Test am 29.09.2026).
 *
 * **Das Problem:** Anna hatte zuerst gar nicht gefunden, dass man angeben kann, wie viele
 * Personen auf einem Bild zu sehen sind. Die Zeile steht im Umfahren dauerhaft da und ist
 * seit demselben Tag hervorgehoben, sobald der Umriss steht (`src/Personenzahl.tsx`) — aber
 * der Normalfall ist 1, und eine Voreinstellung, die meistens stimmt, wird nie angesehen.
 *
 * **Warum einmal am Ende und nicht bei jedem Foto:** eine Rückfrage beim Speichern jedes
 * Bildes wäre bei 60 Fotos 60 zusätzliche Tipps — und eine Rückfrage, die immer kommt, wird
 * weggetippt, nicht gelesen. Hier steht sie genau einmal, an der Stelle, an der ohnehin
 * jemand das Ganze ansieht.
 *
 * **Der tragende Satz ist der für den Fall, dass alles auf 1 steht.** Wer nie etwas
 * eingestellt hat, liest hier zum ersten Mal, dass es etwas einzustellen gab. Deshalb ist
 * er kein Nebensatz, sondern die Aussage des Bildschirms.
 *
 * ⚠️ **Geldwirksam:** die Personenzahl trägt in V1 den Preis (`shared/personen.ts`). Der
 * Prototyp rechnet ausdrücklich keinen Betrag — gezählt wird, gerechnet nicht.
 *
 * Jeder Text ist deutsch, auch jeder Hinweis (`CLAUDE.md`).
 */

interface Props {
  bilder: readonly BildAusListe[];
  /** Weiter in die Rahmenvorschau. Der Aufrufer merkt sich, dass für diesen Stand
   *  geprüft wurde — sonst käme dieser Bildschirm bei jedem Blick wieder. */
  onWeiter: () => void;
  /** Zurück in die Reihe, um eine Zahl zu ändern. */
  onZurueck: () => void;
}

export default function Zaehlpruefung({ bilder, onWeiter, onZurueck }: Props) {
  const gezaehlt = zaehlung(bilder);
  // Der Platz in der Reihe, nicht die Bild-ID: „Bild 3" ist dieselbe Zählung wie am
  // Auswahlring und in der Überschrift von „Bild bearbeiten" (`src/Zuordnung.tsx`).
  const mehrere = mehrfachbilder(bilder);

  return (
    <section className="flex flex-col gap-4 rounded-lg bg-neutral-100 px-4 py-5">
      <h3 className="text-lg font-medium text-neutral-900">Stimmt die Zählung?</h3>

      {mehrere.length === 0 ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-neutral-800">
            Auf jedem deiner {String(gezaehlt.bildelemente)} Bilder ist{" "}
            <strong className="font-medium">eine</strong> Person oder ein Tier gezählt.
          </p>
          {/* Der Satz nennt die Fälle beim Namen. „Prüfe die Personenzahl" wäre die
              kürzere Fassung und die nutzlosere: wer nicht weiß, dass es die Angabe
              gibt, weiß auch nicht, wonach er sucht. */}
          <p className="text-sm text-neutral-700">
            Ist auf einem mehr zu sehen — ein Gruppenfoto, zwei Kinder, der Hund mit dabei? Dann
            tippe das Bild an und dann auf „Bild bearbeiten".
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-neutral-800">
            Auf diesen Bildern ist mehr als eine Person oder ein Tier gezählt:
          </p>
          <ul className="flex flex-col gap-1">
            {mehrere.map((bild) => (
              <li key={bild.bildId} className="text-sm text-neutral-800">
                Bild {String(bild.nummer)}
                {bild.beschriftung === "" ? "" : ` (${bild.beschriftung})`}:{" "}
                {personenText(bild.personen)}
              </li>
            ))}
          </ul>
          <p className="text-sm text-neutral-700">
            Auf allen übrigen ist eine Person oder ein Tier gezählt. Fehlt eines, tippe es an und
            dann auf „Bild bearbeiten".
          </p>
        </div>
      )}

      {/* Die Summe steht auch in der Kopfzeile darüber — hier noch einmal, weil sie das
          ist, was bestätigt wird. Ohne sie bestätigte jemand eine Aussage über einzelne
          Bilder und nicht die Zahl, die zählt. */}
      <p className="text-base font-medium text-neutral-900">
        Zusammen: {String(gezaehlt.personen)} von {String(MAX_PERSONEN)} Personen oder Tieren,
        verteilt auf {String(gezaehlt.bildelemente)}{" "}
        {gezaehlt.bildelemente === 1 ? "Bild" : "Bilder"}.
      </p>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onZurueck}
          className="min-h-14 flex-1 rounded-xl border border-neutral-900 px-4 text-base font-medium text-neutral-900"
        >
          Nochmal ansehen
        </button>
        <button
          type="button"
          onClick={onWeiter}
          className="min-h-14 flex-1 rounded-xl bg-neutral-900 px-4 text-base font-medium text-white"
        >
          Passt, weiter
        </button>
      </div>
    </section>
  );
}
